import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Flower2, ArrowLeft, CheckCircle } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { lessonsAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const LessonView = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { updateUser, user } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    const fetchLesson = async () => {
      try {
        const response = await lessonsAPI.getById(lessonId);
        setLesson(response.data.lesson);
      } catch (error) {
        console.error('Error fetching lesson:', error);
        toast({
          title: 'Erreur',
          description: 'Impossible de charger la leçon',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchLesson();
  }, [lessonId]);

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const response = await lessonsAPI.complete(lessonId);
      toast({
        title: 'Leçon terminée!',
        description: `Vous avez gagné ${response.data.pointsEarned} points!`
      });
      
      // Update user points
      if (user) {
        updateUser({
          ...user,
          points: response.data.totalPoints,
          level: response.data.level
        });
      }
      
      // Check if there's a quiz for this lesson
      try {
        const token = localStorage.getItem('token');
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const quizResponse = await fetch(`${BACKEND_URL}/api/lessons/${lessonId}/quiz`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (quizResponse.ok) {
          // Navigate to quiz
          toast({
            title: 'Quiz disponible!',
            description: 'Testez vos connaissances pour gagner 30 points supplémentaires!'
          });
          setTimeout(() => {
            navigate(`/lesson/${lessonId}/quiz`);
          }, 1500);
        } else {
          // No quiz, go back to module
          setTimeout(() => {
            navigate(`/module/${lesson.moduleId}`);
          }, 1500);
        }
      } catch (quizError) {
        // No quiz, go back to module
        setTimeout(() => {
          navigate(`/module/${lesson.moduleId}`);
        }, 1500);
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Erreur lors de la complétion',
        variant: 'destructive'
      });
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  if (!lesson) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 pb-24">
      {/* Header */}
      <div className="sticky top-0 bg-purple-900/80 backdrop-blur-md border-b border-white/10 px-4 py-4 z-40">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/module/${lesson.moduleId}`)} className="text-pink-300">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <Flower2 className="w-8 h-8 text-pink-300" />
          <h1 className="text-xl font-bold text-white">Leçon {lesson.order}</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Lesson Header */}
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold text-white mb-3">{lesson.title}</h2>
          <p className="text-pink-200">{lesson.description}</p>
          <div className="flex items-center justify-center gap-2 mt-4 text-pink-300 text-sm">
            <span>{lesson.duration}</span>
            <span>•</span>
            <span>{lesson.points} points</span>
          </div>
        </div>

        {/* Lesson Image */}
        {lesson.image && (
          <Card className="overflow-hidden bg-white/10 backdrop-blur-md border-white/20">
            <img 
              src={lesson.image} 
              alt={lesson.title}
              className="w-full h-64 object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </Card>
        )}

        {/* Lesson Video */}
        {lesson.video_url && lesson.video_url !== 'None' && (
          <Card className="overflow-hidden bg-white/10 backdrop-blur-md border-white/20 relative">
            {lesson.video_url.includes('youtube.com') || lesson.video_url.includes('youtu.be') ? (
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src={lesson.video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                  title="Video educative"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                />
              </div>
            ) : (
              <video 
                src={lesson.video_url} 
                controls
                className="w-full"
                poster={lesson.image}
                onError={(e) => { e.target.parentElement.style.display = 'none'; }}
              />
            )}
          </Card>
        )}

        {/* Lesson Content - Direct content field */}
        {lesson.content && (
          <Card className="p-6 bg-white/10 backdrop-blur-md border-white/20">
            <div className="text-pink-100 text-lg leading-relaxed whitespace-pre-line prose prose-invert max-w-none">
              {lesson.content.split('\n').map((paragraph, idx) => (
                <p key={`p-${idx}`} className="mb-3">
                  {paragraph.split(/\*\*(.*?)\*\*/g).map((part, i) =>
                    i % 2 === 1 ? <strong key={`b-${idx}-${i}`}>{part}</strong> : <span key={`t-${idx}-${i}`}>{part}</span>
                  )}
                </p>
              ))}
            </div>
          </Card>
        )}

        {/* Lesson Contents Array (legacy support) */}
        {lesson.contents && lesson.contents.sort((a, b) => a.order - b.order).map((content) => (
          <Card key={content.id || `content-${content.order}`} className="p-6 bg-white/10 backdrop-blur-md border-white/20">
            {content.type === 'text' && (
              <div className="text-pink-100 text-lg leading-relaxed whitespace-pre-line">
                {content.content}
              </div>
            )}
            {content.type === 'image' && (
              <img src={content.content} alt="Lesson visual" className="w-full rounded-lg" />
            )}
          </Card>
        ))}

        {/* Complete Button */}
        <Card className="p-6 bg-gradient-to-br from-green-500/20 to-emerald-500/20 backdrop-blur-md border-green-400/30">
          <div className="text-center mb-4">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <h3 className="text-xl font-bold text-white mb-2">Leçon terminée?</h3>
            <p className="text-pink-100 text-sm">
              Marquez cette leçon comme complétée pour gagner {lesson.points} points!
            </p>
          </div>
          <Button 
            onClick={handleComplete}
            disabled={completing}
            className="w-full bg-gradient-to-r from-green-400 to-emerald-500 hover:from-green-500 hover:to-emerald-600 text-white font-semibold py-4 rounded-xl disabled:opacity-50"
          >
            {completing ? 'Enregistrement...' : 'Terminer la leçon'}
          </Button>
        </Card>
      </div>
    </div>
  );
};

export default LessonView;
